import {all, any, each, filter, map, reduce} from '../lang/array';
import {isEq, isFunction} from '../lang/is';
import {noop} from '../lang/function';
import {Collection} from '../structures/collection';

const
    Edge = class Edge {
        constructor(from, to, listener) {
            this.from = from;
            this.to = to;
            this.outgoing = from.outgoing.add(this);
            this.incoming = to.incoming.add(this);
            this.listener = listener;
        }

        remove() {
            if (this.outgoing && this.incoming) {
                const listener = this.listener;

                this.outgoing.remove();
                this.incoming.remove();
                if (listener) {
                    listener();
                }
                this.from = null;
                this.to = null;
                this.outgoing = null;
                this.incoming = null;
                this.listener = null;
            }
        }
    },

    graph = new Collection(),

    Vertex = class Vertex {
        constructor(value) {
            this.value = value;
            this.entry = graph.add(this);
            this.outgoing = new Collection();
            this.incoming = new Collection();
            this.token = null;
        }

        remove() {
            if (this.entry && this.outgoing && this.incoming) {
                this.entry.remove();
                this.outgoing.each(edge => {
                    edge.remove();
                });
                this.incoming.each(edge => {
                    edge.remove();
                });
                this.value = null;
                this.entry = null;
                this.outgoing = null;
                this.incoming = null;
                this.token = null;
            }
        }

        children() {
            if (this.entry) {
                const
                    token = {},
                    values = [];

                (function recurse(vertex) {
                    vertex.token = token;
                    // NOTE (mkibbel): This is the hottest path, so we manually inline the loop:
                    //
                    // vertex.outgoing.each(edge => {
                    //     if (edge.to.token !== token) {
                    //         recurse(edge.to);
                    //     }
                    // });
                    for (
                        let i = 0, entries = vertex.outgoing.entries, length = entries.length;
                        i < length;
                        i += 1) {

                        const entry = entries[i];

                        if (entry) {
                            const vertex = entry.value.to;

                            if (vertex.token !== token) {
                                recurse(vertex);
                            }
                        }
                    }
                    values.push(vertex.value);
                }(this));
                return values.reverse();
            } else {
                return [];
            }
        }

        traverse(visit) {
            const
                children = this.children(),
                length = children.length;

            for (let i = 0; i < length; i += 1) {
                visit(children[i]);
            }
        }
    },

    Scheduler = class Scheduler {
        constructor() {
            this.tasks = new Collection();
            this.promise = null;
        }

        schedule(task) {
            const scheduled = this.tasks.add(task);

            if (!this.promise) {
                this.promise = (
                    Promise.resolve()
                    .then(() => {
                        while (this.tasks.count) {
                            const tasks = this.tasks;

                            this.tasks = new Collection();
                            tasks.each(task => {
                                task();
                            });
                        }
                        this.promise = null;
                    }));
            }
            return scheduled.remove.bind(scheduled);
        }

        ready() {
            return (
                Promise.resolve()
                .then(() => this.promise));
        }
    },

    scheduler = new Scheduler(),

    schedule = scheduler.schedule.bind(scheduler),

    ready = scheduler.ready.bind(scheduler),

    connect = (parent, child) => {
        if (parent.completed) {
            throw new Error('Cannot connect to a completed stream');
        }
        parent.initialize(child);
        if (!parent.vertex) {
            parent.vertex = new Vertex(parent.update.bind(parent));
            parent.connect();
        }
        return (
            new Edge(
                parent.vertex,
                child.vertex,
                () => {
                    const vertex = parent.vertex;

                    if (vertex && vertex.outgoing.count === 0) {
                        parent.disconnect();
                        parent.vertex = null;
                        vertex.remove();
                        parent.tasks.each(cancel => {
                            cancel();
                        });
                        parent.tasks = new Collection();
                    }
                }));
    },

    next = (stream, value) => {
        if (!stream.completed) {
            const
                cancel = schedule(() => {
                    const token = {};

                    task.remove();
                    stream.token = token;
                    stream.value = value;
                    if (stream.vertex) {
                        stream.vertex.traverse(update => {
                            update(token);
                        });
                    }
                }),
                task = stream.tasks.add(cancel);
        }
    },

    complete = stream => {
        if (!stream.completed) {
            const
                cancel = schedule(() => {
                    const token = {};

                    task.remove();
                    stream.completed = true;
                    stream.disconnect();
                    if (stream.vertex) {
                        stream.vertex.traverse(update => {
                            update(token);
                        });
                        if (stream.vertex) {
                            stream.vertex.outgoing.each(edge => {
                                edge.remove();
                            });
                        }
                    }
                }),
                task = stream.tasks.add(cancel);
        }
    },

    Stream = class Stream {
        constructor() {
            this.token = null;
            this.value = null;
            this.completed = false;
            this.vertex = null;
            this.tasks = new Collection();
        }

        connect() {}

        disconnect() {}

        initialize() {}

        update() {}

        complete() {
            this.disconnect();
            complete(this);
        }

        subscribe(fn, complete) {
            let drain = null,
                cancel = (
                    schedule(() => {
                        cancel = null;
                        drain = (
                            new Drain(
                                new Tap(this, fn),
                                complete));
                    }));

            return () => {
                if (cancel) {
                    cancel();
                    cancel = null;
                }
                if (drain) {
                    drain.close();
                    drain = null;
                }
            };
        }

        latest(persistent) {
            return new Latest(this, persistent);
        }

        tap(fn) {
            return new Tap(this, fn);
        }

        map(fn) {
            return new Map(this, fn);
        }

        filter(fn) {
            return new Filter(this, fn);
        }

        scan(fn, initial, persistent) {
            return new Scan(this, fn, initial, persistent);
        }

        unique(fn, persistent) {
            return new Unique(this, fn, persistent);
        }

        take(count) {
            return new Take(this, count);
        }

        flatCombineAny() {
            return new FlatCombineAny(this);
        }

        flatCombineAll() {
            return new FlatCombineAll(this);
        }

        flatMerge() {
            return new FlatMerge(this);
        }

        select() {
            return new Select(this);
        }

        static of(values) {
            return new Of(values);
        }

        static generate(fn, initial) {
            return new Generate(fn, initial);
        }

        static combineAny(streams) {
            return new CombineAny(streams);
        }

        static combineAll(streams) {
            return new CombineAll(streams);
        }

        static merge(streams) {
            return new Merge(streams);
        }
    },

    Drain = class Drain {
        constructor(stream, fn) {
            this.stream = stream;
            this.vertex = null;
            this.fn = (
                fn ?
                    () => {
                        try {
                            fn();
                        } catch (error) {
                            console.error('Uncaught exception in Stream.subscribe():', error);
                        }
                    } :
                    noop);
            if (!stream.completed) {
                this.vertex = new Vertex(this.update.bind(this));
                connect(stream, this);
            } else {
                const fn = this.fn;

                this.vertex = null;
                fn();
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.close();
            }
        }

        close() {
            const
                fn = this.fn,
                vertex = this.vertex;

            if (vertex) {
                this.vertex = null;
                vertex.remove();
                fn();
            }

        }
    },

    Source = class Source extends Stream {
        next(value) {
            next(this, value);
        }

        complete() {
            complete(this);
        }
    },

    Of = class Of extends Stream {
        constructor(values) {
            super();
            this.completed = values.length === 0;
            this.values = values;
            this.index = 0;
        }

        connect() {
            const
                values = this.values,
                index = this.index;

            if (index === values.length) {
                complete(this);
            } else {
                next(this, values[index]);
            }
        }

        update(token) {
            const
                values = this.values,
                index = this.index + 1;

            this.index = index;
            if (index === values.length) {
                complete(this);
            } else {
                next(this, values[index]);
            }
        }
    },

    Generate = class Generate extends Stream {
        constructor(fn, initial) {
            super();
            this.value = initial;
            this.completed = (initial === undefined);
            this.fn = () => {
                try {
                    return fn(this.value);
                } catch (error) {
                    console.error('Uncaught exception in Stream.generate():', error);
                }
            };
        }

        connect() {
            if (this.value === undefined) {
                complete(this);
            } else {
                next(this, this.value);
            }
        }

        update(token) {
            const
                fn = this.fn,
                value = fn(this.value);

            if (value === undefined) {
                complete(this);
            } else {
                next(this, value);
            }
        }
    },

    Latest = class Latest extends Stream {
        constructor(stream, persistent) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.initializers = null;
            this.connection = null;
            this.persistent = persistent || false;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.initializers) {
                this.initializers.each(cancel => {
                    cancel();
                });
                this.initializers = null;
            }
            if (!this.persistent) {
                this.token = null;
            }
        }

        initialize(stream) {
            if (!stream.completed && this.token) {
                const
                    initializers = this.initializers || (this.initializers = new Collection()),
                    cancel = schedule(() => {
                        const token = {};

                        task.remove();
                        initializer.remove();
                        this.token = token;
                        stream.vertex.traverse(update => {
                            update(token);
                        });
                    }),
                    task = stream.tasks.add(cancel),
                    initializer = initializers.add(cancel);
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                if (this.initializers) {
                    this.initializers.each(cancel => {
                        cancel();
                    });
                    this.initializers = null;
                }
                this.token = token;
                this.value = stream.value;
            }
        }
    },

    Tap = class Tap extends Stream {
        constructor(stream, fn) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.fn = value => {
                try {
                    fn(value);
                } catch (error) {
                    console.error('Uncaught exception in Stream.tap():', error);
                }
            };
            this.connection = null;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                const fn = this.fn;

                this.token = token;
                this.value = stream.value;
                fn(stream.value);
            }
        }
    },

    Map = class Map extends Stream {
        constructor(stream, fn) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.fn = value => {
                try {
                    return fn(value);
                } catch (error) {
                    console.error('Uncaught exception in Stream.map():', error);
                }
            };
            this.connection = null;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                const fn = this.fn;

                this.token = token;
                this.value = fn(stream.value);
            }
        }
    },

    Filter = class Filter extends Stream {
        constructor(stream, fn) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.fn = value => {
                try {
                    return fn(value);
                } catch (error) {
                    console.error('Uncaught exception in Stream.filter():', error);
                }
            };
            this.connection = null;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                const
                    fn = this.fn,
                    value = stream.value;

                if (fn(value)) {
                    this.token = token;
                    this.value = value;
                }
            }
        }
    },

    Scan = class Scan extends Stream {
        constructor(stream, fn, initial, persistent) {
            super();
            this.stream = stream;
            this.fn = (previous, value) => {
                try {
                    return fn(previous, value);
                } catch (error) {
                    console.error('Uncaught exception in Stream.scan():', error);
                }
            };
            this.initial = (
                isFunction(initial) ?
                    () => {
                        try {
                            return initial();
                        } catch (error) {
                            console.error('Uncaught exception in Stream.initial():', error);
                        }
                    } :
                    () => initial);
            this.cancel = null;
            this.connection = null;
            this.persistent = persistent || false;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                if (!this.token && !this.cancel) {
                    const
                        initial = this.initial,
                        cancel = schedule(() => {
                            const token = {};

                            task.remove();
                            this.token = token;
                            this.cancel = null;
                            this.vertex.traverse(update => {
                                update(token);
                            });
                        }),
                        task = this.tasks.add(cancel);

                    this.cancel = cancel;
                    this.value = initial();
                }
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.cancel) {
                const cancel = this.cancel;

                this.cancel = null;
                cancel();
            }
            if (!this.persistent) {
                this.token = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                const fn = this.fn;

                if (this.cancel) {
                    const cancel = this.cancel;

                    this.cancel = null;
                    cancel();
                }
                this.token = token;
                this.value = fn(this.value, stream.value);
            }
        }
    },

    Unique = class Unique extends Stream {
        constructor(stream, fn = isEq, persistent) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.fn = (next, previous) => {
                try {
                    return fn(next, previous);
                } catch (error) {
                    console.error('Uncaught exception in Stream.unique():', error);
                }
            };
            this.persistent = persistent || false;;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (!this.persistent) {
                this.token = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                const
                    fn = this.fn,
                    value = stream.value;

                if (!this.token || !fn(value, this.value)) {
                    this.token = token;
                    this.value = value;
                }
            }
        }
    },

    Take = class Take extends Stream {
        constructor(stream, count) {
            super();
            this.completed = stream.completed || count === 0;
            this.stream = stream;
            this.count = count;
            this.connection = null;
        }

        connect() {
            if (this.count === 0 || this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed) {
                this.completed = true;
                this.disconnect();
            } else if (stream.token === token) {
                this.token = token;
                this.value = stream.value;
                if (this.count > 0) {
                    this.count -= 1;
                    if (this.count === 0) {
                        this.complete();
                    }
                }
            }
        }
    },

    CombineAny = class CombineAny extends Stream {
        constructor(streams) {
            super();
            this.completed = any(streams, stream => stream.completed);
            this.streams = streams;
            this.connections = null;
        }

        connect() {
            if (any(this.streams, stream => stream.completed)) {
                complete(this);
            } else {
                this.connections = map(this.streams, stream => connect(stream, this));
            }
        }

        disconnect() {
            if (this.connections) {
                each(this.connections, connection => {
                    connection.remove();
                });
                this.connections = null;
            }
        }

        update(token) {
            const
                streams = this.streams;

            if (any(streams, stream => stream.completed)) {
                this.completed = true;
                this.disconnect();
            } else if (any(streams, stream => stream.token === token)) {
                this.token = token;
                this.value = map(streams, stream => stream.value);
            }
        }
    },

    CombineAll = class CombineAll extends Stream {
        constructor(streams) {
            super();
            this.completed = any(streams, stream => stream.completed);
            this.streams = streams;
            this.connections = null;
        }

        connect() {
            if (any(this.streams, stream => stream.completed)) {
                complete(this);
            } else {
                this.connections = map(this.streams, stream => connect(stream, this));
            }
        }

        disconnect() {
            if (this.connections) {
                each(this.connections, connection => {
                    connection.remove();
                });
                this.connections = null;
            }
        }

        update(token) {
            const
                streams = this.streams;

            if (any(streams, stream => stream.completed)) {
                this.completed = true;
                this.disconnect();
            } else if (
                any(streams, stream => stream.token === token) &&
                all(streams, stream => stream.token)) {

                this.token = token;
                this.value = map(streams, stream => stream.value);
            }
        }
    },

    Merge = class Merge extends Stream {
        constructor(streams) {
            super();
            this.completed = !streams.length || all(streams, stream => stream.completed);
            this.streams = streams;
            this.merged = [];
        }

        connect() {
            if (all(this.streams, stream => stream.completed)) {
                complete(this);
            } else {
                this.merged = (
                    reduce(
                        this.streams,
                        (merged, stream) => {
                            if (!stream.completed) {
                                merged.push({
                                    stream,
                                    connection: connect(stream, this)
                                });
                            }
                            return merged;
                        },
                        []));
            }
        }

        disconnect() {
            if (this.merged.length) {
                each(this.merged, ({connection}) => {
                    connection.remove();
                });
                this.merged = [];
            }
        }

        update(token) {
            if (all(this.merged, ({stream}) => stream.completed)) {
                this.completed = true;
                this.disconnect();
            } else {
                if (any(this.merged, ({stream}) => stream.completed)) {
                    this.merged = (
                        filter(
                            this.merged,
                            ({stream, connection}) => {
                                if (stream.completed) {
                                    connection.remove();
                                    return false;
                                } else {
                                    return true;
                                }
                            }));
                }
                const
                    updated = (
                        reduce(
                            this.merged,
                            (updated, {stream}) => {
                                if (stream.token && stream.token === token) {
                                    updated.push(stream.value);
                                }
                                return updated;
                            },
                            []));

                if (updated.length) {
                    this.token = token;
                    this.value = updated;
                }
            }
        }
    },

    FlatCombineAny = class FlatCombineAny extends Stream {
        constructor(stream) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.connection = null;
            this.combined = [];
        }

        connect() {
            if (this.stream.completed && (
                    !this.combined.length ||
                    all(this.combined, ({stream}) => stream.completed))) {

                complete(this);
            } else {
                if (!this.stream.completed) {
                    this.connection = connect(this.stream, this);
                }
                this.combined = (
                    reduce(
                        this.combined,
                        (combined, {stream}) => {
                            if (!stream.completed) {
                                combined.push({
                                    stream,
                                    connection: connect(stream, this)
                                });
                            }
                            return combined;
                        },
                        []));
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.combined.length) {
                each(this.combined, combined => {
                    if (combined.connection) {
                        combined.connection.remove();
                        combined.connection = null;
                    }
                });
            }
        }

        update(token) {
            if (this.stream.completed && (
                    !this.combined.length ||
                    all(this.combined, ({stream}) => stream.completed))) {

                this.completed = true;
                this.disconnect();
            } else {
                if (any(this.combined, ({stream}) => stream.completed)) {
                    this.combined = (
                        filter(
                            this.combined,
                            ({stream, connection}) => {
                                if (stream.completed) {
                                    connection.remove();
                                    return false;
                                } else {
                                    return true;
                                }
                            }));
                }
                if (!this.stream.completed && this.stream.token === token) {
                    const stream = this.stream.value;

                    if (!stream.completed) {
                        this.combined.push({
                            stream,
                            connection: connect(stream, this)
                        });
                    }
                }
                if (this.combined.length &&
                    any(this.combined, ({stream}) => stream.token === token)) {

                    this.token = token;
                    this.value = map(this.combined, ({stream}) => stream.value);
                }
            }
        }
    },

    FlatCombineAll = class FlatCombineAll extends Stream {
        constructor(stream) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.connection = null;
            this.combined = [];
        }

        connect() {
            if (this.stream.completed && (
                    !this.combined.length ||
                    all(this.combined, ({stream}) => stream.completed))) {

                complete(this);
            } else {
                if (!this.stream.completed) {
                    this.connection = connect(this.stream, this);
                }
                this.combined = (
                    reduce(
                        this.combined,
                        (combined, {stream}) => {
                            if (!stream.completed) {
                                combined.push({
                                    stream,
                                    connection: connect(stream, this)
                                });
                            }
                            return combined;
                        },
                        []));
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.combined.length) {
                each(this.combined, combined => {
                    if (combined.connection) {
                        combined.connection.remove();
                        combined.connection = null;
                    }
                });
            }
        }

        update(token) {
            if (this.stream.completed && (
                    !this.combined.length ||
                    all(this.combined, ({stream}) => stream.completed))) {

                this.completed = true;
                this.disconnect();
            } else {
                if (any(this.combined, ({stream}) => stream.completed)) {
                    this.combined = (
                        filter(
                            this.combined,
                            ({stream, connection}) => {
                                if (stream.completed) {
                                    connection.remove();
                                    return false;
                                } else {
                                    return true;
                                }
                            }));
                }
                if (!this.stream.completed && this.stream.token === token) {
                    const stream = this.stream.value;

                    if (!stream.completed) {
                        this.combined.push({
                            stream,
                            connection: connect(stream, this)
                        });
                    }
                }
                if (this.combined.length && (
                    all(this.combined, ({stream}) => stream.token) &&
                    any(this.combined, ({stream}) => stream.token === token))) {

                    this.token = token;
                    this.value = map(this.combined, ({stream}) => stream.value);
                }
            }
        }
    },

    FlatMerge = class FlatMerge extends Stream {
        constructor(stream) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.connection = null;
            this.merged = [];
        }

        connect() {
            if (this.stream.completed && (
                    !this.merged.length ||
                    all(this.merged, ({stream}) => stream.completed))) {

                complete(this);
            } else {
                if (!this.stream.completd) {
                    this.connection = connect(this.stream, this);
                }
                this.merged = (
                    reduce(
                        this.merged,
                        (merged, {stream}) => {
                            if (!stream.completed) {
                                merged.push({
                                    stream,
                                    connection: connect(stream, this)
                                });
                            }
                            return merged;
                        },
                        []));
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.merged.length) {
                each(this.merged, merged => {
                    if (merged.connection) {
                        merged.connection.remove();
                        merged.connection = null;
                    }
                });
            }
        }

        update(token) {
            if (this.stream.completed && (
                    !this.merged.length ||
                    all(this.merged, ({stream}) => stream.completed))) {

                this.completed = true;
                this.disconnect();
            } else {
                if (any(this.merged, ({stream}) => stream.completed)) {
                    this.merged = (
                        filter(
                            this.merged,
                            ({stream, connection}) => {
                                if (stream.completed) {
                                    connection.remove();
                                    return false;
                                } else {
                                    return true;
                                }
                            }));
                }
                if (!this.stream.completed && this.stream.token === token) {
                    const stream = this.stream.value;

                    if (!stream.completed) {
                        this.merged.push({
                            stream,
                            connection: connect(stream, this)
                        });
                    }
                }
                if (this.merged.length) {
                    const
                        updated = (
                            reduce(
                                this.merged,
                                (updated, {stream}) => {
                                    if (stream.token === token) {
                                        updated.push(stream.value);
                                    }
                                    return updated;
                                },
                                []));

                    if (updated.length) {
                        this.token = token;
                        this.value = updated;
                    }
                }
            }
        }
    },

    Select = class Select extends Stream {
        constructor(stream) {
            super();
            this.completed = stream.completed;
            this.stream = stream;
            this.connection = null;
            this.selected = null;
        }

        connect() {
            if (this.stream.completed) {
                complete(this);
            } else {
                this.connection = connect(this.stream, this);
                if (this.selected) {
                    this.selected.connection = connect(this.selected.stream, this);
                }
            }
        }

        disconnect() {
            if (this.connection) {
                this.connection.remove();
                this.connection = null;
            }
            if (this.selected) {
                this.selected.connection.remove();
                this.selected.connection = null;
            }
        }

        update(token) {
            const stream = this.stream;

            if (stream.completed && (
                    !this.selected ||
                    this.selected.stream.completed)) {

                this.completed = true;
                this.disconnect();
            } else {
                if (this.selected && (
                        this.selected.stream.completed || (
                            !stream.completed &&
                            stream.token === token))) {

                    this.selected.connection.remove();
                    this.selected = null;
                }
                if (!stream.completed && stream.token === token) {
                    const value = stream.value;

                    if (!value.completed) {
                        this.selected = {
                            stream: value,
                            connection: connect(value, this)
                        };
                    }
                    this.token = null;
                }
                if (this.selected && this.selected.stream.token === token) {
                    this.token = token;
                    this.value = this.selected.stream.value;
                }
            }
        }
    };

export {
    Stream,
    Source,
    ready
};
